/**
 * Per-tab body cappers for `sessionPersistenceCap` — split out of it when the
 * shape guards + the Table Designer / Spatial Explorer sites pushed that module
 * past the repo's 200-line budget. The policy, the rationale and the TOTALITY
 * contract live in `sessionPersistenceCap.ts`; this file is only the per-type
 * mechanics plus the `capTab` dispatcher.
 *
 * Two rules decide the shape of every capper here:
 *
 * 1. OMIT, never truncate. A shorter body is a DIFFERENT body.
 * 2. A placeholder may only land where it cannot be EXECUTED or WRITTEN OUT.
 *    A query tab, a notebook cell and a VQE buffer can come back as a comment
 *    (restore detaches them from their file first). A Table Designer buffer
 *    and a Spatial layer query cannot — both are executed — so those omit the
 *    FIELD and let the feature re-derive or fail visibly.
 */

import type {
  SerializedNotebookTabContent,
  SerializedPlSqlDebuggerTabContent,
  SerializedQueryTabContent,
  SerializedSpatialExplorerTabContent,
  SerializedTab,
  SerializedTableDesignerTabContent,
  SerializedVisualQueryTabContent,
} from '../types/sessionTypes';
import type { SpatialLayerDefinition } from '../../workspace/spatial/types/spatialTypes';
import { exceedsPersistedSqlLimit } from '../../../types/editorTextLimits';

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * A tab's `content` when it really is a plain object, `null` otherwise. The
 * persisted snapshot is user-writable JSON that a previous version (or a hand
 * edit) may have left without a `content`, so the declared union is a claim,
 * not a guarantee — see TOTALITY in `sessionPersistenceCap.ts`.
 */
function contentRecordOf(tab: SerializedTab): Record<string, unknown> | null {
  const raw: unknown = tab?.content;
  return isRecord(raw) ? raw : null;
}

function capQueryTab(tab: SerializedTab): SerializedTab {
  const record = contentRecordOf(tab);
  if (!record) return tab;
  const content = record as SerializedQueryTabContent;
  if (!exceedsPersistedSqlLimit(content.query)) return tab;
  const { fileLastSavedHash: _dropped, ...rest } = content;
  void _dropped;
  return { ...tab, content: { ...rest, query: '', queryOmitted: true } };
}

function capNotebookTab(tab: SerializedTab): SerializedTab {
  const record = contentRecordOf(tab);
  if (!record) return tab;
  const content = record as SerializedNotebookTabContent;
  if (!Array.isArray(content.cells)) return tab;
  let omitted = false;
  const cells = content.cells.map((cell) => {
    // A malformed persisted cell (null, a string, a number) is kept verbatim:
    // it holds no oversized body to drop, and reading `.source` off it would
    // throw inside `partialize`.
    if (!isRecord(cell)) return cell;
    if (!exceedsPersistedSqlLimit(cell.source)) return cell;
    omitted = true;
    return { ...cell, source: '', sourceOmitted: true };
  });
  if (!omitted) return tab;
  // Same invariant as a query tab's `fileLastSavedHash`: a notebook missing a
  // cell's source must never look in sync with the file on disk.
  const { lastSavedHash: _dropped, ...rest } = content;
  void _dropped;
  return { ...tab, content: { ...rest, cells } };
}

// Legacy shape only: no runtime tab is a visual-query tab any more, but saved
// sessions from older versions keep theirs until restored (`tabDeserializer`).
function capVisualQueryTab(tab: SerializedTab): SerializedTab {
  const record = contentRecordOf(tab);
  if (!record) return tab;
  const content = record as SerializedVisualQueryTabContent;
  if (!exceedsPersistedSqlLimit(content.query)) return tab;
  return { ...tab, content: { ...content, query: '', queryOmitted: true } };
}

function capPlSqlDebuggerTab(tab: SerializedTab): SerializedTab {
  const record = contentRecordOf(tab);
  if (!record) return tab;
  const content = record as SerializedPlSqlDebuggerTabContent;
  if (!exceedsPersistedSqlLimit(content.source)) return tab;
  const { source: _dropped, ...rest } = content;
  void _dropped;
  return { ...tab, content: { ...rest, sourceOmitted: true } };
}

/**
 * Table Designer — `currentSql` is a user-authored buffer (View SQL is an
 * editor) and `initialSql` is the baseline Save diffs it against. Every field
 * below is dropped, never substituted, so that no DDL the user did not write
 * can reach the database; `Docs/QueryEditor.md` carries the long form.
 *   - `currentSql` goes whenever EITHER buffer is over the cap. `canSave`
 *     enables Save on any buffer differing from the baseline, so a placeholder
 *     here would plan a migration dropping every column. The tab's bootstrap
 *     re-derives it (from `initialSql` in alter mode, the blank template in
 *     create mode), landing on "No changes to save".
 *   - `initialSql` goes only when IT is oversized — it is what alter mode
 *     re-derives from, and dropping it alone leaves dirtiness computed against
 *     nothing.
 *   - `isDirty` + `columnRenames` describe a draft that no longer exists.
 *   - the LEGACY fields go too (`columns` back to `[]`, `originalState`
 *     dropped). Both legacy predicates key on "no buffers + legacy fields
 *     present", so keeping them would make a capped tab restore as a
 *     pre-buffer draft: a buffer synthesized from OLD form-state edits, an
 *     unequal pair, Save ENABLED, banner replaced. Reviving a previous intent
 *     for a tab whose latest intent we just dropped is worse than offering
 *     nothing. Uncapped tabs keep both fields and their recovery path.
 */
function capTableDesignerTab(tab: SerializedTab): SerializedTab {
  const record = contentRecordOf(tab);
  if (!record) return tab;
  const content = record as SerializedTableDesignerTabContent;
  const baselineOversized = exceedsPersistedSqlLimit(content.initialSql);
  if (!baselineOversized && !exceedsPersistedSqlLimit(content.currentSql)) return tab;
  const {
    currentSql: _currentDropped,
    initialSql: _baseline,
    isDirty: _dirtyDropped,
    columnRenames: _renamesDropped,
    originalState: _legacyState,
    ...rest
  } = content;
  void _currentDropped;
  void _dirtyDropped;
  void _renamesDropped;
  void _legacyState;
  return {
    ...tab,
    content: {
      ...rest,
      // `columns` is a required field on the serialized shape, so it is emptied
      // rather than omitted — `hasLegacyDesignerDraft` reads its LENGTH for a
      // create draft, so `[]` is what makes a capped create tab non-legacy.
      columns: [],
      // Re-added only when it was there AND small: a create-mode tab has no
      // baseline at all, and `initialSql: undefined` would add a key that
      // `JSON.stringify` drops anyway.
      ...(!baselineOversized && _baseline !== undefined ? { initialSql: _baseline } : {}),
      sqlOmitted: true,
    },
  };
}

/**
 * Spatial Explorer — a `kind: 'query'` layer stores the user's SQL verbatim in
 * `view.layers[].source.sql` and runs it on activate. An oversized query is
 * dropped to `''` and flagged; `loadQuerySourceLayer` then refuses to execute
 * the layer and surfaces the omission on the layer row. The layer itself is
 * KEPT — with its label, style and cap — because a layer that silently
 * disappears from the list is exactly the "empty map, no explanation" outcome
 * the flag exists to prevent, and the row is what the user needs in order to
 * re-add it. Table-backed layers carry no body and are never touched.
 */
function capSpatialExplorerTab(tab: SerializedTab): SerializedTab {
  const record = contentRecordOf(tab);
  if (!record) return tab;
  const content = record as SerializedSpatialExplorerTabContent;
  const view = content.view;
  if (!isRecord(view) || !Array.isArray(view.layers)) return tab;
  let omitted = false;
  const layers = (view.layers as SpatialLayerDefinition[]).map((layer) => {
    if (!isRecord(layer)) return layer;
    const source = layer.source;
    if (!isRecord(source) || source.kind !== 'query') return layer;
    if (!exceedsPersistedSqlLimit(source.sql as string | undefined)) return layer;
    omitted = true;
    return { ...layer, source: { ...source, sql: '', sqlOmitted: true } } as SpatialLayerDefinition;
  });
  if (!omitted) return tab;
  return { ...tab, content: { ...content, view: { ...view, layers } as typeof content.view } };
}

export function capTab(tab: SerializedTab): SerializedTab {
  try {
    if (tab?.type === 'query') return capQueryTab(tab);
    if (tab?.type === 'notebook') return capNotebookTab(tab);
    if (tab?.type === 'visual-query') return capVisualQueryTab(tab);
    if (tab?.type === 'pl-sql-debugger') return capPlSqlDebuggerTab(tab);
    if (tab?.type === 'table-designer') return capTableDesignerTab(tab);
    if (tab?.type === 'spatial-explorer') return capSpatialExplorerTab(tab);
    return tab;
  } catch (error) {
    // Last resort for a shape none of the guards above anticipated: persist the
    // tab uncapped rather than crash the write (TOTALITY, `sessionPersistenceCap`).
    console.warn('[sessionPersistenceCap] left a tab uncapped — unexpected shape', error);
    return tab;
  }
}
